import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useStaticQuery } from 'gatsby';
import { Brand, Link } from '@printdeal/ui-library';
import clsx from 'clsx';
import {
  dispatchDataLayerKiyohClick,
  KiyohEventAction,
} from '../../../../helpers/dataLayerHelpers/HomepageDataLayerHelper';
import { Kiyoh, ReviewUrl } from '../../../../types/Kiyoh';

interface KiyohQueryData {
  kiyoh?: Kiyoh;
}

/** @knipignore */
export const kiyohData = graphql`
  fragment kiyohData on Kiyoh {
    last12MonthAverageRating
    last12MonthNumberReviews
    reviewURLs {
      locale
      reviewUrl
    }
  }
`;

const kiyohQuery = graphql`
  query KiyohQuery {
    kiyoh {
      ...kiyohData
    }
  }
`;

interface KiyohScoreLinkProps {
  className?: string;
}

const getReviewUrl = (reviewURLs: ReviewUrl[], locale: string) => {
  const { reviewUrl } = reviewURLs.find((element: ReviewUrl) => element.locale === locale) || {};
  return reviewUrl;
};

const KiyohScoreLink = ({ className }: KiyohScoreLinkProps) => {
  const {
    kiyoh: {
      last12MonthAverageRating,
      last12MonthNumberReviews,
      reviewURLs = [],
    } = {},
  } = useStaticQuery<KiyohQueryData>(kiyohQuery);
  const intl = useIntl();
  const reviewUrl = getReviewUrl(reviewURLs, intl.locale);

  const linkProps = useMemo(() => ({
    target: '_blank',
    rel: 'noreferrer nofollow noopener',
    href: reviewUrl,
    onClick: () => dispatchDataLayerKiyohClick(KiyohEventAction.header),
  }), [reviewUrl]);

  const isLink = !!reviewUrl;

  if (!last12MonthAverageRating || !last12MonthNumberReviews) return null;

  return (
    <Link
      as={isLink ? 'a' : 'span'}
      variant="secondary"
      iconBefore={(
        <Brand
          brand="kiyoh"
          aria-label={intl.formatMessage({ id: 'kiyoh.score.screenReaderLabel', defaultMessage: 'Kiyoh score' })}
          className="pd-h-5 pd-shrink-0"
        />
      )}
      className={clsx('pd-whitespace-nowrap pd-text-sm', className)}
      {...(isLink ? linkProps : {})}
    >
      {last12MonthAverageRating > 0 && last12MonthAverageRating}
      {' '}
      Kiyoh
      {' '}
      {last12MonthNumberReviews > 0 && (
        <>
          ({last12MonthNumberReviews}
          {' '}
          <FormattedMessage id="usp.reviews" defaultMessage="reviews" />)
        </>
      )}
    </Link>
  );
};

export default KiyohScoreLink;
